import { Link } from 'react-router-dom';
import { Avatar } from '@/components/ui/Avatar';
import { Card, CardBody } from '@/components/ui/Card';
import { Rating } from '@/components/ui/Rating';
import { StatusBadge } from '@/components/ui/StatusBadge';
import type { Ride, RouteMatch } from '@/types/models';
import { formatDate, formatMoney, formatTime } from '@/utils/format';

const NEAR_ROUTE_THRESHOLD_KM = 0.3;

/** Describes how close an along-route search's pickup/drop sat to this ride's route --
 * exactly on it reads as "on your route", not "0.0 km off". */
function nearRouteLabel(match: RouteMatch): string {
  const worst = Math.max(match.pickupOffRouteKm, match.dropOffRouteKm);
  return worst <= NEAR_ROUTE_THRESHOLD_KM ? 'On your route' : `${worst.toFixed(1)} km from your route`;
}

export interface RideCardProps {
  ride: Ride;
  showStatus?: boolean;
  /** The leg this passenger searched for, by gazetteer name. Carried into the ride link
   * so the detail page prices and books the same leg the card advertised, rather than
   * quietly reverting to the driver's whole route. */
  segment?: { pickup: string; drop: string };
}

/** The search-result / list card: driver, times, route, price, seats. */
export function RideCard({ ride, showStatus = false, segment }: RideCardProps) {
  const href = segment
    ? `/rides/${ride.rideId}?pickup=${encodeURIComponent(segment.pickup)}&drop=${encodeURIComponent(segment.drop)}`
    : `/rides/${ride.rideId}`;
  return (
    <Link to={href} className="block rounded-card transition-shadow hover:shadow-raised focus-visible:shadow-raised"
      aria-label={`${ride.origin.shortName} to ${ride.destination.shortName}, departs ${formatTime(ride.departureAt)}, ${formatMoney(ride.pricePerSeatMinor, ride.currency)} per seat`}>
      <Card>
        <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="text-right">
              <p className="text-lg font-semibold text-slate-900">{formatTime(ride.departureAt)}</p>
              <p className="text-sm text-slate-500">{formatTime(ride.estimatedArrivalAt)}</p>
            </div>
            <div className="flex flex-col items-center pt-2" aria-hidden="true">
              <span className="size-2.5 rounded-full border-2 border-brand-700" />
              <span className="my-1 h-8 w-px bg-slate-300" />
              <span className="size-2.5 rounded-full bg-brand-700" />
            </div>
            <div>
              <p className="font-medium text-slate-900">{ride.origin.shortName}</p>
              <p className="mt-6 font-medium text-slate-900">{ride.destination.shortName}</p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-6 sm:flex-col sm:items-end sm:gap-2">
            <div className="text-right">
              <p className="text-xl font-semibold text-brand-800">
                {formatMoney(ride.routeMatch?.segmentPricePerSeatMinor ?? ride.pricePerSeatMinor, ride.currency)}
              </p>
              <p className="text-xs text-slate-500">
                {ride.routeMatch ? `per seat for your ${Math.round(ride.routeMatch.segmentDistanceKm)} km` : 'per seat'}
                {' '}&middot; {formatDate(ride.departureAt)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {showStatus && <StatusBadge status={ride.status} />}
              <span className="text-sm text-slate-600">{ride.availableSeats} of {ride.totalSeats} seats left</span>
            </div>
          </div>
        </CardBody>
        <div className="flex items-center gap-3 border-t border-slate-100 px-5 py-3 sm:px-6">
          <Avatar name={ride.driver.name} src={ride.driver.photoUrl} size="sm" />
          <span className="text-sm font-medium text-slate-800">{ride.driver.name}</span>
          <Rating value={ride.driver.ratingAverage} count={ride.driver.ratingCount} />
          {ride.instantBooking && (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              Instant booking
            </span>
          )}
          {ride.routeMatch && (
            <span className="ml-auto text-xs text-slate-500">{nearRouteLabel(ride.routeMatch)}</span>
          )}
          {ride.originDistanceKm !== undefined && !ride.routeMatch && (
            <span className="ml-auto text-xs text-slate-500">{ride.originDistanceKm.toFixed(1)} km from your start</span>
          )}
        </div>
      </Card>
    </Link>
  );
}
